'use client';

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAppStore } from '@/store/app';
import Link from 'next/link';
import EventPing from '@/components/EventPing';
import { buildCoinRoute } from '@/lib/instrument';

interface CrossAsset {
  symbol: string;
  name: string;
  type: 'crypto' | 'stock' | 'index' | 'commodity' | 'fx';
  price: number;
  changeAbs: number;
  changePct: number;
  currency: string;
  spark: number[];
  updatedAt: number;
  /** AUD19: quote timestamp from the source (Yahoo regularMarketTime / HL now) */
  sourceAsOf?: number;
  /** AUD19: REGULAR | CLOSED | PRE | POST (raw source field) */
  marketState?: string;
  /** D07: market session mapped from the source field — never inferred from refresh time */
  marketSession?: 'regular' | 'preMarket' | 'afterHours' | 'closed' | 'unknown';
  /** D07: quote health — fresh / delayed / stale / missing, computed server-side */
  dataHealth?: 'fresh' | 'delayed' | 'stale' | 'missing';
  /** D07: quote as-of (ms) and fetch time (ms) are two distinct concepts */
  quoteAt?: number | null;
  fetchedAt?: number;
  /** D21: change base — crypto uses "24h", traditional assets use "previous close" */
  changeBase?: '24h' | 'previous_close' | string;
}

const MARKET_STATE_LABEL = (state: string, zh: boolean): string => {
  switch (state) {
    case 'regular': return zh ? '交易中' : 'Open';
    case 'preMarket': return zh ? '盘前' : 'Pre-market';
    case 'afterHours': return zh ? '盘后' : 'After-hours';
    case 'closed': return zh ? '休市' : 'Closed';
    default: return zh ? '时段未知' : 'Unknown';
  }
};

const DATA_HEALTH_LABEL: Record<string, { zh: string; en: string; cls: string }> = {
  fresh: { zh: '即时', en: 'Fresh', cls: 'bg-emerald-400/10 text-emerald-400' },
  delayed: { zh: '延迟', en: 'Delayed', cls: 'bg-amber-400/10 text-amber-400' },
  stale: { zh: '数据陈旧', en: 'Stale', cls: 'bg-orange-400/15 text-orange-400' },
  missing: { zh: '数据缺失', en: 'No data', cls: 'bg-gf-surface2 text-gf-muted' },
};

const fmtUsd = (n: number): string => {
  if (!isFinite(n)) return '—';
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(2)}K`;
  if (n >= 1) return `$${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  return `$${n.toLocaleString(undefined, { maximumFractionDigits: 4 })}`;
};

/** AUD03: quote by asset type — indexes are points (no $), FX is a quote/base
 *  rate (5 sig), commodities stay USD per unit, crypto/stock keep $ amounts. */
const fmtByType = (t: string, n: number, zh: boolean): string => {
  if (!isFinite(n)) return '—';
  if (t === 'index') return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
  if (t === 'fx') return n.toLocaleString(undefined, { maximumFractionDigits: 5 });
  if (t === 'commodity') return `$${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}${zh ? ' /桶' : '/bbl'}`;
  return fmtUsd(n);
};

/** D21: explicit price unit label per asset type. */
const unitLabel = (t: string, zh: boolean): string => {
  if (t === 'index') return zh ? '点' : 'pts';
  if (t === 'fx') return zh ? '汇率' : 'rate';
  if (t === 'commodity') return zh ? 'USD/桶' : 'USD/bbl';
  return 'USD';
};

/** D21: spark 首尾点收益（图内区间），仅供参考，不可与其他资产直接排名比较。 */
const sparkReturnPct = (spark: number[]): number | null => {
  if (!spark || spark.length < 2) return null;
  const a = spark[0];
  const b = spark[spark.length - 1];
  if (!isFinite(a) || !isFinite(b) || a <= 0) return null;
  return ((b - a) / a) * 100;
};

const typeLabel = (t: string, zh: boolean): string => {
  if (zh) {
    return t === 'crypto' ? '加密' : t === 'stock' ? '股票' : t === 'index' ? '指数' : t === 'commodity' ? '大宗商品' : '外汇';
  }
  return t === 'crypto' ? 'Crypto' : t === 'stock' ? 'Stock' : t === 'index' ? 'Index' : t === 'commodity' ? 'Commodity' : 'FX';
};

const sourceLabel = (t: string, zh: boolean): string => {
  if (t === 'crypto') return zh ? '链上 / 现货参考' : 'On-chain / spot ref';
  return zh ? 'Yahoo Finance' : 'Yahoo Finance';
};

const TYPE_ORDER = ['crypto', 'stock', 'index', 'commodity', 'fx'] as const;

function Sparkline({ data, up }: { data: number[]; up: boolean }) {
  if (!data || data.length < 2) {
    return <div className="h-10 w-full rounded bg-gf-surface2" />;
  }
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const w = 120;
  const h = 36;
  const pts = data
    .map((v, i) => {
      const x = (i / (data.length - 1)) * w;
      const y = h - 3 - ((v - min) / range) * (h - 6);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  const color = up ? '#10b981' : '#ef4444';
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="h-10 w-full" preserveAspectRatio="none" aria-hidden>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
      <polygon points={`0,${h} ${pts} ${w},${h}`} fill={color} opacity="0.08" />
    </svg>
  );
}

function AssetIcon({ symbol, type }: { symbol: string; type: string }) {
  // Crypto assets use their symbol chip; traditional assets use a monogram tile.
  const initials =
    symbol === 'SPX' ? 'S&P' : symbol === 'BRENT' ? 'BR' : symbol === 'EURUSD' ? '€$' : symbol.slice(0, 3);
  const styles: Record<string, string> = {
    crypto: 'bg-amber-400/20 text-amber-500',
    stock: 'bg-blue-400/20 text-blue-400',
    index: 'bg-violet-400/20 text-violet-400',
    commodity: 'bg-orange-400/20 text-orange-400',
    fx: 'bg-emerald-400/20 text-emerald-400',
  };
  return (
    <span
      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-xs font-bold ${styles[type] ?? styles.crypto}`}
    >
      {initials}
    </span>
  );
}

const assetHref = (symbol: string): string => {
  if (symbol === 'HYPE') return '/hyperliquid';
  // D01：现货参考必须指向现货身份，不能默认解析为永续合约
  if (symbol === 'BTC') return buildCoinRoute({ symbol: 'BTCUSDT', exchange: 'Binance', product: 'spot' });
  return '';
};

/* ----------------------------- N04 比较研究 ----------------------------- */

interface CompareSeriesPoint {
  t: number;
  close: number;
  retPct: number;
  index: number;
}
interface CompareCell {
  r: number | null;
  n: number;
  whole?: number | null;
  recent?: number | null;
  constant: boolean;
  disabledReason?: 'insufficient_samples' | 'constant_series';
}
interface CompareResult {
  meta: { quote: string; range: string; interval: string; method: string; indexing: string; notes: string[] };
  series: Record<string, CompareSeriesPoint[]>;
  correlation: Record<string, Record<string, CompareCell>>;
  samples: { common: number; coverage: Record<string, number>; range: { start: number; end: number } };
}

const COMPARE_PRESETS = ['BTCUSDT', 'ETHUSDT', 'SOLUSDT', 'BNBUSDT', 'XRPUSDT', 'DOGEUSDT', 'ADAUSDT', 'AVAXUSDT', 'LINKUSDT', 'PEPEUSDT'];

const RANGE_OPTIONS = [
  { v: '7d', zh: '7 天', en: '7d' },
  { v: '30d', zh: '30 天', en: '30d' },
  { v: '90d', zh: '90 天', en: '90d' },
];
const INTERVAL_OPTIONS = [
  { v: '1h', zh: '1 小时', en: '1h' },
  { v: '4h', zh: '4 小时', en: '4h' },
  { v: '1d', zh: '日线', en: '1d' },
];

/** N04: 相关性着色 —— 正相关绿、负相关红，强度映射透明度 */
const corrColor = (r: number | null): string => {
  if (r === null) return 'transparent';
  const abs = Math.min(1, Math.abs(r));
  const alpha = 0.08 + abs * 0.32;
  return r >= 0 ? `rgba(16,185,129,${alpha.toFixed(3)})` : `rgba(239,68,68,${alpha.toFixed(3)})`;
};

const corrLabel = (c: CompareCell, zh: boolean): string => {
  if (c.r === null || c.r === undefined) {
    return zh ? (c.constant ? '不可计算' : '—') : c.constant ? 'N/A' : '—';
  }
  return c.r.toFixed(2);
};

function CompareCurve({ series, zh, height = 220 }: { series: CompareResult['series']; zh: boolean; height?: number }) {
  const syms = Object.keys(series);
  if (!syms.length) {
    return <div className={`h-[${height}px] rounded-lg bg-gf-surface2`} />;
  }
  const w = 640;
  const h = height;
  const allPts = syms.flatMap((s) => series[s].map((p) => p.index));
  const min = Math.min(...allPts);
  const max = Math.max(...allPts);
  const range = max - min || 1;
  const pad = 6;
  const len = series[syms[0]].length;
  const colors = ['#f59e0b', '#10b981', '#3b82f6', '#a855f7', '#ec4899'];

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full" preserveAspectRatio="none" aria-label={zh ? '标准化收益曲线' : 'Normalized return curves'}>
      {[0.25, 0.5, 0.75].map((f) => {
        const y = pad + (h - pad * 2) * f;
        return <line key={f} x1="0" y1={y} x2={w} y2={y} stroke="rgba(255,255,255,0.06)" strokeWidth="1" />;
      })}
      {syms.map((s, si) => {
        const pts = series[s]
          .map((p, i) => {
            const x = (i / Math.max(1, len - 1)) * w;
            const y = pad + (h - pad * 2) * (1 - (p.index - min) / range);
            return `${x.toFixed(1)},${y.toFixed(1)}`;
          })
          .join(' ');
        return (
          <polyline
            key={s}
            points={pts}
            fill="none"
            stroke={colors[si % colors.length]}
            strokeWidth="1.8"
            strokeLinejoin="round"
            strokeLinecap="round"
            opacity="0.95"
          />
        );
      })}
    </svg>
  );
}

/** N04: 曲线图例与资产标签 */
function CompareLegend({ syms }: { syms: string[] }) {
  const colors = ['#f59e0b', '#10b981', '#3b82f6', '#a855f7', '#ec4899'];
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1.5">
      {syms.map((s, i) => (
        <span key={s} className="flex items-center gap-1.5 text-[11px] text-gf-muted">
          <span className="inline-block h-2 w-3 rounded-sm" style={{ background: colors[i % colors.length] }} />
          {s}
        </span>
      ))}
    </div>
  );
}

export default function CrossAssetsPage() {
  const { language } = useAppStore();
  const zh = language !== 'en';

  const [cmpSyms, setCmpSyms] = useState<string[]>(['BTCUSDT', 'ETHUSDT', 'SOLUSDT']);
  const [cmpRange, setCmpRange] = useState('30d');
  const [cmpInterval, setCmpInterval] = useState('4h');
  const [searchQ, setSearchQ] = useState('');

  const { data: searchRes } = useQuery({
    queryKey: ['compare-search', searchQ],
    queryFn: () => api.get<Array<{ symbol: string; exchange: string; product: string }>>(`/market/search?q=${encodeURIComponent(searchQ)}`),
    enabled: searchQ.trim().length >= 2,
    staleTime: 30_000,
  });

  const cmpKey = cmpSyms.length >= 2 ? cmpSyms.slice().sort().join(',') : '';
  const { data: cmp, isFetching: cmpFetching, error: cmpError } = useQuery({
    queryKey: ['compare', cmpKey, cmpRange, cmpInterval],
    queryFn: () => api.get<CompareResult>(`/market/compare?symbols=${encodeURIComponent(cmpKey)}&range=${cmpRange}&interval=${cmpInterval}`),
    enabled: !!cmpKey,
    staleTime: 60_000,
  });

  const { data, isFetching } = useQuery({
    queryKey: ['cross-assets'],
    queryFn: () => api.get<CrossAsset[]>('/market/cross-assets'),
    refetchInterval: 10_000,
    staleTime: 5_000,
  });

  const updatedAt = data?.length ? data[0].updatedAt : 0;
  const updatedStr = updatedAt
    ? new Date(updatedAt).toLocaleTimeString(zh ? 'zh-Hant' : 'en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    : '—';

  // R13 VERIFY: freshness gate — a quote older than this is labelled stale, and
  // never counts toward the LIVE banner. D07: health comes from the server.
  const liveOk = (data ?? []).filter((a) => a.marketSession === 'regular' && a.type !== 'crypto').every(
    (a) => a.dataHealth === 'fresh' || a.dataHealth === 'delayed',
  );

  const toggleSym = (s: string) => {
    setCmpSyms((prev) => {
      if (prev.includes(s)) return prev.filter((x) => x !== s);
      if (prev.length >= 5) return prev;
      return [...prev, s];
    });
  };

  const syms = Object.keys(cmp?.series ?? {});
  const cmpSymsSorted = cmpKey ? cmpKey.split(',') : [];

  return (
    <div className="mx-auto max-w-[1200px] px-3 py-4 sm:px-6">
      <EventPing event="cross_asset_view" />
      {/* N04 比较研究 */}
      <div className="mb-6 rounded-xl border border-gf-border bg-gf-surface p-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="text-base font-bold text-gf-text">{zh ? '统一收益与相关性比较' : 'Unified Return & Correlation'}</h2>
            <p className="mt-0.5 text-[11px] text-gf-muted">
              {zh
                ? '选择 2-5 个同报价币资产，价格收益对齐后比较（指数化起点 100）'
                : 'Pick 2-5 assets with the same quote currency; price returns are aligned (indexed to 100)'}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {RANGE_OPTIONS.map((o) => (
              <button
                key={o.v}
                onClick={() => setCmpRange(o.v)}
                className={`rounded-md px-2.5 py-1 text-xs ${cmpRange === o.v ? 'bg-gf-accent/20 text-gf-accent' : 'bg-gf-surface2 text-gf-muted hover:text-gf-text'}`}
              >
                {zh ? o.zh : o.en}
              </button>
            ))}
            <span className="mx-1 h-4 w-px bg-gf-border" />
            {INTERVAL_OPTIONS.map((o) => (
              <button
                key={o.v}
                onClick={() => setCmpInterval(o.v)}
                className={`rounded-md px-2.5 py-1 text-xs ${cmpInterval === o.v ? 'bg-gf-accent/20 text-gf-accent' : 'bg-gf-surface2 text-gf-muted hover:text-gf-text'}`}
              >
                {zh ? o.zh : o.en}
              </button>
            ))}
          </div>
        </div>

        {/* 资产选择 */}
        <div className="mb-3 flex flex-wrap items-center gap-1.5">
          {COMPARE_PRESETS.map((s) => {
            const on = cmpSyms.includes(s);
            return (
              <button
                key={s}
                onClick={() => toggleSym(s)}
                className={`rounded-lg px-2 py-1 text-[11px] font-medium transition-colors ${
                  on ? 'bg-gf-accent/20 text-gf-accent' : 'bg-gf-surface2 text-gf-muted hover:text-gf-text'
                }`}
              >
                {s}
              </button>
            );
          })}
          <span className="text-[10px] text-gf-muted/70">{cmpSyms.length}/5</span>
        </div>

        {/* 搜索添加 */}
        <div className="relative mb-3">
          <input
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            placeholder={zh ? '输入币种关键词搜寻并添加（如 ETH）…' : 'Search a coin to add (e.g. ETH)…'}
            className="input h-9 w-full rounded-lg bg-gf-surface2 px-3 text-xs text-gf-text placeholder:text-gf-muted/60 focus:outline-none focus:ring-1 focus:ring-gf-accent/50"
          />
          {searchRes && searchRes.length > 0 && searchQ.trim().length >= 2 && (
            <div className="absolute left-0 right-0 top-full z-20 mt-1 max-h-52 overflow-auto rounded-lg border border-gf-border bg-gf-surface shadow-xl">
              {searchRes.slice(0, 8).map((r) => {
                const sym = r.symbol;
                const on = cmpSyms.includes(sym);
                return (
                  <button
                    key={`${r.exchange}-${r.product}-${sym}`}
                    onClick={() => {
                      toggleSym(sym);
                      setSearchQ('');
                    }}
                    className={`flex w-full items-center justify-between px-3 py-2 text-left text-xs hover:bg-gf-surface2 ${on ? 'text-gf-accent' : 'text-gf-text'}`}
                  >
                    <span className="font-medium">{sym}</span>
                    <span className="rounded bg-gf-surface2 px-1.5 py-0.5 text-[10px] text-gf-muted">
                      {r.product === 'perpetual' ? (zh ? '合约' : 'Perp') : (zh ? '现货' : 'Spot')}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* 主图 */}
        {cmpError ? (
          <div className="rounded-lg border border-red-400/20 bg-red-400/5 px-3 py-2.5 text-xs text-red-400">
            {String((cmpError as Error)?.message ?? cmpError)}
          </div>
        ) : cmp && syms.length >= 2 ? (
          <>
            <div className="mb-1">
              <CompareCurve series={cmp.series} zh={zh} />
              <div className="mt-1.5">
                <CompareLegend syms={syms} />
              </div>
            </div>

            {/* 相关矩阵 */}
            <div className="mt-4 overflow-x-auto">
              <div className="mb-1 text-[11px] font-semibold text-gf-muted">{zh ? '相关矩阵（价格收益对齐）' : 'Correlation matrix (aligned price returns)'}</div>
              <table className="border-separate border-spacing-1">
                <thead>
                  <tr>
                    <th className="px-1 py-0.5 text-left text-[10px] font-medium text-gf-muted" />
                    {cmpSymsSorted.map((s) => (
                      <th key={s} className="px-1 py-0.5 text-center text-[10px] font-medium text-gf-muted">{s}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {cmpSymsSorted.map((a) => (
                    <tr key={a}>
                      <td className="px-1 py-0.5 text-right text-[10px] font-medium text-gf-text">{a}</td>
                      {cmpSymsSorted.map((b) => {
                        const cell = cmp.correlation[a]?.[b];
                        const r = cell?.r ?? null;
                        const title = cell
                          ? zh
                            ? `${a} × ${b}\n相关系数: ${r === null ? '不可计算' : r.toFixed(4)}\n共同样本: ${cell.n}\n全区间: ${cell.whole === null || cell.whole === undefined ? '—' : cell.whole.toFixed(3)}\n最近半窗: ${cell.recent === null || cell.recent === undefined ? '—' : cell.recent.toFixed(3)}${cell.constant ? '\n序列为常数，相关不可计算' : ''}`
                            : `${a} × ${b}\nCorr: ${r === null ? 'N/A' : r.toFixed(4)}\nSamples: ${cell.n}\nWhole: ${cell.whole ?? '—'}\nRecent half: ${cell.recent ?? '—'}${cell.constant ? '\nConstant series — not computable' : ''}`
                          : '';
                        return (
                          <td key={b} title={title} className="min-w-[54px] rounded-md px-1.5 py-1 text-center text-[11px] font-medium tabular-nums text-gf-text" style={{ background: corrColor(r) }}>
                            {corrLabel(cell ?? { r: null, n: 0, constant: false }, zh)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* 样本表 */}
            <div className="mt-3 overflow-x-auto">
              <div className="mb-1 text-[11px] font-semibold text-gf-muted">{zh ? '样本与覆盖' : 'Samples & coverage'}</div>
              <table className="w-full border-separate border-spacing-0 rounded-lg text-xs">
                <thead>
                  <tr className="bg-gf-surface2 text-left text-[10px] text-gf-muted">
                    <th className="rounded-l-md px-2.5 py-1.5 font-medium">{zh ? '资产' : 'Asset'}</th>
                    <th className="px-2.5 py-1.5 text-right font-medium">{zh ? '共同样本' : 'Common samples'}</th>
                    <th className="px-2.5 py-1.5 text-right font-medium">{zh ? '覆盖率' : 'Coverage'}</th>
                    <th className="rounded-r-md px-2.5 py-1.5 text-right font-medium">{zh ? '区间收益（指数化）' : 'Interval return (indexed)'}</th>
                  </tr>
                </thead>
                <tbody>
                  {cmpSymsSorted.map((s) => {
                    const arr = cmp.series[s] ?? [];
                    const first = arr[0];
                    const last = arr[arr.length - 1];
                    const totalRet = first && last && first.index > 0 ? ((last.index - first.index) / first.index) * 100 : null;
                    const cov = cmp.samples.coverage[s] ?? 0;
                    return (
                      <tr key={s} className="border-b border-gf-border/50 last:border-0">
                        <td className="px-2.5 py-1.5 font-medium text-gf-text">{s}</td>
                        <td className="px-2.5 py-1.5 text-right tabular-nums text-gf-muted">{arr.length}</td>
                        <td className="px-2.5 py-1.5 text-right tabular-nums text-gf-muted">{cov}%</td>
                        <td className={`px-2.5 py-1.5 text-right font-semibold tabular-nums ${totalRet === null ? 'text-gf-muted' : totalRet >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {totalRet === null ? '—' : `${totalRet >= 0 ? '+' : ''}${totalRet.toFixed(2)}%`}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* 计算说明（可展开） */}
            <details className="mt-3">
              <summary className="cursor-pointer text-[11px] text-gf-accent">
                {zh ? '计算说明（点击展开）' : 'Method notes (click to expand)'}
              </summary>
              <ul className="mt-1.5 list-inside list-disc space-y-0.5 text-[10px] leading-relaxed text-gf-muted">
                {(cmp.meta.notes ?? []).map((n, i) => (
                  <li key={i}>{zh ? n : n}</li>
                ))}
              </ul>
            </details>

            <div className="mt-2 text-[10px] text-gf-muted/70">
              {zh
                ? '相关性仅描述历史同步运动，不代表因果关系，也不构成任何「低相关即安全」的保证。'
                : 'Correlation describes historical co-movement only — it is not causation and offers no "low correlation = safe" guarantee.'}
            </div>
          </>
        ) : cmpFetching ? (
          <div className="py-6 text-center text-xs text-gf-muted">{zh ? '计算中…' : 'Computing…'}</div>
        ) : (
          <div className="py-6 text-center text-xs text-gf-muted">
            {zh ? '请至少选择 2 个资产进行比较' : 'Select at least 2 assets to compare'}
          </div>
        )}
      </div>

      {/* Header */}
      <div className="mb-4 flex items-center justify-between gap-2">
        <div>
          <h1 className="text-xl font-bold text-gf-text sm:text-2xl">
            {zh ? '跨资产对比' : 'Cross-Asset Comparison'}
          </h1>
          <p className="mt-0.5 text-xs text-gf-muted sm:text-sm">
            {zh
              ? '链上资产与全球主流资产同屏实时对照 · 数据源 Yahoo Finance（免费）'
              : 'On-chain & global assets side by side in real time · Data: Yahoo Finance (free)'}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className={`rounded-full px-2.5 py-0.5 text-[11px] ${isFetching ? 'bg-gf-surface2 text-gf-muted' : liveOk ? 'bg-emerald-400/10 text-emerald-400' : 'bg-gf-surface2 text-gf-muted'}`}>
            {isFetching
              ? (zh ? '更新中…' : 'Refreshing…')
              : liveOk
                ? '● LIVE'
                : (zh ? '● 部分休市 / 延迟' : '● Mixed / delayed')}
          </span>
          <span className="text-[11px] text-gf-muted">{zh ? '页面更新于' : 'Page updated'} {updatedStr}</span>
        </div>
      </div>

      {/* Grouped by type — same column order: name / price / change / updated / source */}
      {TYPE_ORDER.map((tp) => {
        const items = (data ?? []).filter((a) => a.type === tp);
        if (!items.length) return null;
        return (
          <div key={tp} className="mb-6">
            <div className="mb-2 flex items-center gap-2">
              <span className="rounded-md bg-gf-accent/10 px-2 py-0.5 text-xs font-semibold text-gf-accent">{typeLabel(tp, zh)}</span>
              <span className="text-[11px] text-gf-muted">{items.length} {zh ? '项' : 'items'}</span>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {items.map((a) => {
                const up = a.changePct >= 0;
                const href = assetHref(a.symbol);
                const sparkRet = sparkReturnPct(a.spark);
                const sparkUp = (sparkRet ?? 0) >= 0;
                const baseLabel = a.changeBase === '24h' ? (zh ? '24h 涨跌' : '24h') : a.changeBase === 'previous_close' ? (zh ? '前收涨跌' : 'Prev close') : null;
                // AUD19: quote time (source) vs page refresh time (updatedAt) are distinct.
                // R13 VERIFY: cross-day quotes must show the date; timezone is always shown.
                const d = a.sourceAsOf ? new Date(a.sourceAsOf) : null;
                const _today = new Date();
                const crossDay = !!d && (d.getFullYear() !== _today.getFullYear() || d.getMonth() !== _today.getMonth() || d.getDate() !== _today.getDate());
                const timeStr = d
                  ? (crossDay ? d.toLocaleDateString('en-US', { month: 'short', day: '2-digit' }) + ' ' : '') +
                    d.toLocaleTimeString(zh ? 'zh-Hant' : 'en-US', { hour: '2-digit', minute: '2-digit', hour12: false }) +
                    ' UTC' + (d.getTimezoneOffset() <= 0 ? '+' : '-') + Math.abs(d.getTimezoneOffset() / 60)
                  : '—';
                const state = a.marketSession ?? 'unknown';
                const health = a.dataHealth ?? 'missing';
                const stateZh = zh;
                const stateCls =
                  state === 'regular'
                    ? 'bg-emerald-400/10 text-emerald-400'
                    : state === 'preMarket' || state === 'afterHours'
                      ? 'bg-amber-400/10 text-amber-400'
                      : state === 'closed'
                        ? 'bg-gf-surface2 text-gf-muted'
                        : 'bg-gf-surface2 text-gf-muted';
                const healthCfg = DATA_HEALTH_LABEL[health] ?? DATA_HEALTH_LABEL.missing;
                const inner = (
                  <>
                    <div className="flex items-center gap-2.5">
                      <AssetIcon symbol={a.symbol} type={a.type} />
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="truncate text-sm font-semibold text-gf-text">{a.name}</span>
                          <span className="rounded bg-gf-surface2 px-1.5 py-0.5 text-[10px] text-gf-muted">
                            {a.symbol}
                          </span>
                          {href && (
                            <span className="rounded bg-gf-surface2 px-1.5 py-0.5 text-[10px] text-gf-accent">
                              {a.symbol === 'HYPE' ? (zh ? '链上总览 ↗' : 'On-chain ↗') : (zh ? '行情 ↗' : 'Markets ↗')}
                            </span>
                          )}
                          <span className={`rounded px-1.5 py-0.5 text-[10px] ${stateCls}`}>
                            {MARKET_STATE_LABEL(state, stateZh)}
                          </span>
                          <span className={`rounded px-1.5 py-0.5 text-[10px] ${healthCfg.cls}`}>
                            {zh ? healthCfg.zh : healthCfg.en}
                          </span>
                        </div>
                        <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                          <span className={`text-xs font-semibold tabular-nums ${up ? 'text-emerald-400' : 'text-red-400'}`}>
                            {up ? '+' : ''}{a.changePct.toFixed(2)}%
                          </span>
                          {baseLabel && (
                            <span className="rounded bg-white/5 px-1.5 py-0.5 text-[10px] text-gf-muted">{baseLabel}</span>
                          )}
                          {sparkRet != null && (
                            <span className={`text-[10px] ${sparkUp ? 'text-emerald-400' : 'text-red-400'}`} title={zh ? '图内区间收益（各资产区间时段不同，不可直接排名比较）' : 'Intra-chart return (windows differ per asset, not directly comparable)'}>
                              {sparkUp ? '+' : ''}{sparkRet.toFixed(2)}%{zh ? ' 图内' : ' chart'}
                            </span>
                          )}
                          <span className="text-[10px] text-gf-muted/70">
                            {state === 'regular' && a.type !== 'crypto'
                              ? (zh ? '行情' : 'quote') + ' '
                              : state !== 'unknown'
                                ? (zh ? '最近成交' : 'last') + ' '
                                : ''}
                            {timeStr}
                            {a.type !== 'crypto' && (zh ? ' · ~15min 延迟' : ' · ~15m delayed')}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 flex items-end justify-between gap-2">
                      <div>
                        <div className="flex items-baseline gap-1.5">
                          <span className="text-lg font-bold tabular-nums text-gf-text">{fmtByType(a.type, a.price, zh)}</span>
                          <span className="text-[10px] text-gf-muted">{unitLabel(a.type, zh)}</span>
                        </div>
                        <div className="mt-0.5 text-[10px] text-gf-muted">{sourceLabel(a.type, zh)}</div>
                      </div>
                      <div className="w-28">
                        <Sparkline data={a.spark} up={up} />
                      </div>
                    </div>
                  </>
                );
                return (
                  <div
                    key={a.symbol}
                    className={`rounded-xl border border-gf-border bg-gf-surface p-3.5 transition-shadow ${href ? 'cursor-pointer hover:shadow-lg hover:border-gf-border/80' : ''}`}
                  >
                    {href ? <Link href={href} className="block h-full">{inner}</Link> : inner}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Footnote */}
      <div className="mt-5 rounded-lg border border-gf-border/60 bg-gf-surface/60 px-3 py-2.5 text-[11px] leading-relaxed text-gf-muted">
        {zh
          ? '注：股票 / 指数 / 大宗 / 外汇涨跌基准为「前收」，HYPE / BTC 基准为「24h 前」；各市场交易时段不同，涨跌幅与图内区间收益不可直接并排排名。行情来自 Yahoo Finance 公开接口，实时性以对应市场交易时段为准；HYPE 与 BTC 为链上 / 加密现货参考价（进入具体市场时保持现货身份）。本页仅供信息参考，不构成投资建议。'
          : 'Note: stock / index / commodity / FX change base is previous close; HYPE / BTC base is 24h ago. Markets trade in different sessions, so % change and intra-chart returns are not directly comparable side by side. Quotes come from Yahoo Finance public endpoints; freshness follows each market\'s trading session. HYPE & BTC are on-chain / crypto spot reference prices (keep spot identity when entering a market). For information only — not investment advice.'}
      </div>
    </div>
  );
}
